import { AgentImageService } from '@agent/agent-interface/contact-view/chat/services/agent-image.service';
import { ChatAttachmentService } from '@agent/agent-interface/contact-view/chat/services/chat-attachment.service';
import { ChatEmojiService } from '@agent/agent-interface/contact-view/chat/services/chat-emoji.service';
import { ChatMessageUtilService } from '@agent/agent-interface/contact-view/chat/services/chat-message-util.service';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { SessionDeprecatedService } from '@commons/core/auth/session-deprecated.service';

import { HttpService, ProfileFacade, ProfileService, UntilDestroy, untilDestroyed } from '@contact/upgrade';

import { ActiveRoom } from '../../model/active-room';
import { ChatMessage } from '../../model/chat-message';
import { ChatModel } from '../../model/chat-model';

const MODULE_NAME = 'chatMessage';

@UntilDestroy()
@Component({
  selector: 'chat-message',
  templateUrl: 'chat-message.component.html',
  styleUrls: ['./chat-message.component.scss'],
})
export class ChatMessageComponent implements OnInit {
  public name = MODULE_NAME;

  @Input()
  public msg: ChatMessage;

  @Input()
  public displayCopyLayer: boolean;

  @Input()
  public chat: ChatModel;

  public errorMessage: string | undefined;

  public csrId: string;
  public picture: string;
  public hasChatAssistancePermission: boolean = false;

  @Output()
  burgerClick: EventEmitter<any> = new EventEmitter();

  @Output()
  copyToRoom: EventEmitter<any> = new EventEmitter();

  constructor(
    private readonly emojiService: ChatEmojiService,
    private readonly profileFacade: ProfileFacade,
    private readonly profileService: ProfileService,
    private readonly chatAttachmentService: ChatAttachmentService,
    private readonly agentImageService: AgentImageService,
    private readonly chatMessageUtilService: ChatMessageUtilService,
    private readonly http: HttpService,
    private readonly sessionDeprecatedService: SessionDeprecatedService
  ) {}

  ngOnInit() {
    this.profileFacade.id$.pipe(untilDestroyed(this)).subscribe(id => (this.csrId = id));
    this.profileService.picture$.pipe(untilDestroyed(this)).subscribe(picture => this.agentImageService.setPicture(this.csrId, picture));
    this.hasChatAssistancePermission = this.sessionDeprecatedService.hasPermission('CSR::supervisor:chat_assistance');
  }

  onBurgerClicked(): void {
    if (!this.displayCopyLayer) {
      if (this.burgerClick && this.msg) {
        this.burgerClick.emit(this.msg);
      }
    } else {
      if (this.burgerClick) {
        this.burgerClick.emit(null);
      }
    }
  }

  onCopyToRoom(roomId: string = ''): void {
    if (this.copyToRoom && this.msg) {
      this.copyToRoom.emit({ roomId: roomId, message: this.msg });
      this.burgerClick.emit(null);
    }
  }

  getMessageContent(): string {
    let text = this.chatMessageUtilService.processUrl(this.msg.content);
    this.fetchBotImageIfNeeded(this.msg);
    return this.emojiService.emojify(text);
  }

  private fetchBotImageIfNeeded(chatMessage: ChatMessage) {
    const hasUrl =
      chatMessage &&
      chatMessage.chatbotExchangeMessage &&
      chatMessage.chatbotExchangeMessage.attachment &&
      chatMessage.chatbotExchangeMessage.attachment.type === 'image' &&
      !chatMessage.chatbotExchangeMessage.imageUrl &&
      chatMessage.chatbotExchangeMessage.imageUrl !== '';
    if (hasUrl) {
      this.http
        .withHeaders({
          'Content-Type': 'text/plain; charset=UTF-8',
          Accept: 'text/plain',
        })
        .get(chatMessage.chatbotExchangeMessage!.attachment.url, { responseType: 'text' })
        .subscribe(res => {
          chatMessage.chatbotExchangeMessage!.imageUrl = res as string;
        });
      chatMessage.chatbotExchangeMessage!.imageUrl = '';
    }
  }

  getAgentPicture(): string | undefined {
    return this.agentImageService.getPicture(this.msg.csrId);
  }

  copyItemLabel(room: ActiveRoom): string {
    return room.toGuests.map(guest => `${guest.firstName} ${guest.lastName}`).join(',');
  }

  agentHaveProfilePicture(): boolean {
    return !!this.getAgentPicture();
  }

  downloadFile() {
    if (this.msg.attachment) {
      this.chatAttachmentService.downloadFile(this.msg.attachment.fileDownloadUri).subscribe(
        res => {
          this.chatAttachmentService.downloadBlob(res, (this.msg.attachment && this.msg.attachment.fileName) || '');
          this.errorMessage = undefined;
        },
        () => {
          this.errorMessage = 'loading_error_message';
        }
      );
    }
  }
}
